import { LISTEN_MS, quickIntent, type GameState, type ReplyPayload } from "@squire/core";
import type { ConversationMode } from "../avatar/director.js";
import type { VoiceInput, VoiceOutput } from "../voice/types.js";

export type ConvState = "idle" | "speaking" | "listening" | "thinking";

export interface ConversationDeps {
  input: VoiceInput;
  output: VoiceOutput;
  voiceOn: () => boolean;
  /** Send the user's words to the room; resolves with the reply (or null on timeout). */
  send: (text: string) => Promise<ReplyPayload | null>;
  state: () => GameState | null;
  now: () => number;
  view: {
    say(text: string): void;
    thinking(): void;
    showYou(text: string): void;
    setListening(on: boolean, ms?: number): void;
    showPartial(text: string): void;
    setChips(chips: []): void;
  };
  setMode: (mode: ConversationMode) => void;
  onActivity: () => void;
  /** Microphone problem shown to the user. */
  onVoiceError?: (message: string) => void;
  /** Debug commands ("/anim …"): the reply text, or null when it is not a command. */
  command?: (text: string) => string | null;
}

/** Follow-up questions without touching the screen, but never an endless loop. */
const MAX_AUTO_LISTEN = 3;

/**
 * Speak, then open the mic for ~8 s after a question (SPEC 6.1), send what was heard,
 * speak the reply. The mic is never on while the companion speaks and never outside these windows.
 */
export class ConversationController {
  state: ConvState = "idle";
  private generation = 0;
  private autoListens = 0;

  constructor(private readonly d: ConversationDeps) {}

  get busy(): boolean {
    return this.state !== "idle";
  }

  /** Companion speaks (prompt or reply) and optionally listens afterwards. */
  async speak(text: string, opts: { listen: boolean; voice?: boolean }): Promise<void> {
    const gen = ++this.generation;
    this.d.view.say(text);
    if (opts.voice !== false && this.d.voiceOn()) {
      this.set("speaking");
      await this.d.output.speak(text);
      if (gen !== this.generation) return;
    }
    if (opts.listen && this.autoListens < MAX_AUTO_LISTEN) {
      this.autoListens++;
      await this.listen(LISTEN_MS, gen);
    } else this.set("idle");
  }

  /** Tap on the companion or the "Mów" button always starts listening. */
  async tapToTalk(): Promise<void> {
    if (this.state === "listening") {
      this.d.input.stop();
      return;
    }
    this.d.output.stop();
    this.autoListens = 0;
    await this.listen(LISTEN_MS, ++this.generation);
  }

  async listen(ms: number, gen = ++this.generation): Promise<void> {
    this.set("listening");
    this.d.view.setListening(true, ms);
    const heard = await this.d.input.listen({
      timeoutMs: ms,
      maxMs: 20_000,
      onPartial: (t) => this.d.view.showPartial(t),
      onError: (m) => this.d.onVoiceError?.(m),
    });
    this.d.view.setListening(false);
    if (gen !== this.generation) return;
    if (!heard) {
      this.set("idle");
      return;
    }
    await this.userSays(heard, gen);
  }

  /** Typed or spoken input. */
  async userSays(text: string, gen = ++this.generation): Promise<void> {
    this.d.onActivity();
    this.d.view.setChips([]);
    this.d.view.showYou(text);
    const debug = this.d.command?.(text);
    if (debug != null) {
      this.d.view.say(debug);
      this.set("idle");
      return;
    }
    const s = this.d.state();
    // "Zamknij się" stops speech at once, before the server even answers.
    const quick = s ? quickIntent(text, s, this.d.now()) : null;
    if (quick?.endConversation) this.d.output.stop();
    this.set("thinking");
    this.d.view.thinking();
    const reply = await this.d.send(text);
    if (gen !== this.generation) return;
    if (!reply) {
      await this.speak("Coś mi przerwało. Powiesz to jeszcze raz?", { listen: false });
      return;
    }
    if (reply.endConversation) {
      this.autoListens = MAX_AUTO_LISTEN;
      this.d.view.say(reply.text);
      // A short "Jasne." is fine to say; then silence.
      if (this.d.voiceOn() && reply.text.length <= 12) {
        this.set("speaking");
        await this.d.output.speak(reply.text);
      }
      if (gen === this.generation) this.set("idle");
      this.autoListens = 0;
      return;
    }
    await this.speak(reply.text, { listen: reply.question });
    if (gen === this.generation && this.state === "idle") this.autoListens = 0;
  }

  /** Stop everything (overlay opened, device lost the companion, night). */
  interrupt(): void {
    this.generation++;
    this.d.output.stop();
    this.d.input.stop();
    this.d.view.setListening(false);
    this.autoListens = 0;
    this.set("idle");
  }

  private set(s: ConvState) {
    this.state = s;
    this.d.setMode(
      s === "speaking" ? "talk" : s === "listening" ? "listen" : s === "thinking" ? "think" : null,
    );
  }
}
